// src/modulos/Dashboard/dashboards/TIDashboard.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePresence from '../../hooks/usePresence';
import healthService from '../../services/HealthService';
import errorLoggingService from '../../services/ErrorLoggingService';
import { storage } from '../../firebaseConfig';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, BarChart, Bar, RadialBarChart, RadialBar,
    ComposedChart, Line
} from 'recharts';
import dashboardStyles from './Dashboard.module.css';

// ============================================
// CONSTANTS
// ============================================

const TICKETS_COLLECTION = 'help-desk-tickets';
const DEPLOYMENTS_COLLECTION = 'deployments';
const INCIDENTS_COLLECTION = 'incidents';
const INFRA_METRICS_COLLECTION = 'infra-metrics';
const VULNERABILITIES_COLLECTION = 'vulnerabilities';
const TEAM_MEMBERS_COLLECTION = 'team-members';

const STATUS_OPTIONS = ['Aberto', 'Em Progresso', 'Fechado'];

const PRIORITY_OPTIONS = [
    { value: 'critico', label: 'Crítico', icon: '🔴' },
    { value: 'alto', label: 'Alta', icon: '🟠' },
    { value: 'medio', label: 'Média', icon: '🟡' },
    { value: 'baixo', label: 'Baixa', icon: '🟢' },
];

const IMPACT_OPTIONS = [
    { value: 'todos', label: 'Todos os usuários' },
    { value: 'departamento', label: 'Departamento' },
    { value: 'equipe', label: 'Equipe' },
    { value: 'individual', label: 'Individual' },
];

const URGENCY_OPTIONS = [
    { value: 'imediato', label: 'Imediato', icon: '⏰' },
    { value: 'urgente', label: 'Urgente', icon: '📅' },
    { value: 'normal', label: 'Normal', icon: '📋' },
    { value: 'baixa', label: 'Baixa', icon: '📌' },
];

const CATEGORY_OPTIONS = [
    { value: 'acesso', label: 'Acesso e Permissões', icon: '🔑' },
    { value: 'sistema', label: 'Erro no Sistema', icon: '💻' },
    { value: 'duvida', label: 'Dúvida sobre Funcionalidade', icon: '❓' },
    { value: 'melhoria', label: 'Sugestão de Melhoria', icon: '✨' },
    { value: 'dados', label: 'Problema com Dados', icon: '📊' },
    { value: 'integracao', label: 'Integração com Outros Sistemas', icon: '🔄' },
    { value: 'hardware', label: 'Problema de Hardware', icon: '🖥️' },
    { value: 'rede', label: 'Problema de Rede', icon: '🌐' },
    { value: 'seguranca', label: 'Segurança', icon: '🔒' },
    { value: 'outro', label: 'Outro', icon: '📌' },
];

// ============================================
// HELPERS
// ============================================

const getPriorityDisplay = (value) => {
    const found = PRIORITY_OPTIONS.find(p => p.value === value?.toLowerCase());
    return found || { label: value || 'N/A', icon: '📊' };
};

const getCategoryDisplay = (value) => {
    const found = CATEGORY_OPTIONS.find(c => c.value === value);
    return found || { label: value || 'N/A', icon: '📌' };
};

const getUrgencyDisplay = (value) => {
    const found = URGENCY_OPTIONS.find(u => u.value === value);
    return found || { label: value || 'N/A', icon: '📋' };
};

const getImpactDisplay = (value) => {
    const found = IMPACT_OPTIONS.find(i => i.value === value);
    return found || { label: value || 'N/A' };
};

const getPriorityColor = (priority) => {
    const normalized = priority?.toLowerCase();
    switch (normalized) {
        case 'critico':
        case 'critical':
            return { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' };
        case 'alto':
        case 'high':
            return { bg: '#ffedd5', text: '#9a3412', border: '#fed7aa' };
        case 'medio':
        case 'medium':
            return { bg: '#fef3c7', text: '#92400e', border: '#fde68a' };
        case 'baixo':
        case 'low':
            return { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' };
        default:
            return { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' };
    }
};

const formatTimeSpent = (totalMinutes) => {
    if (!totalMinutes || totalMinutes < 0) return '0 min';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
        return `${hours}h ${minutes}min`;
    }
    return `${minutes} min`;
};

const getTimeInStatus = (ticket) => {
    if (!ticket?.updatedAt?.toDate) return 'N/A';
    const diff = Date.now() - ticket.updatedAt.toDate().getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}min`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
};

// ============================================
// ICONS
// ============================================

const Icons = {
    Database: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>,
    Shield: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
    AlertCircle: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
    CheckCircle: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
    Clock: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    User: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    TrendingUp: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
    TrendingDown: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" /></svg>,
    Refresh: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6" /><path d="M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" /><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" /></svg>,
    Trash: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
    Award: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6" /><path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.11" /></svg>,
    Gauge: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /><path d="M12 2a10 10 0 1 0 0 20" /></svg>,
    Target: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
    DollarSign: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
    Cloud: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" /></svg>,
    Zap: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>,
    ExternalLink: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>,
    X: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
    Download: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
    Upload: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
    File: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><polyline points="13 2 13 9 20 9" /></svg>,
    Folder: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>,
    Filter: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" /></svg>,
    Tag: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>,
    MessageSquare: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
    ClockIcon: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    Users: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
};

const ChartIcons = {
    BarChart: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20V10M18 20V4M6 20v-4" /></svg>,
    PieChart: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>,
    Activity: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
};

// ============================================
// UI COMPONENTS
// ============================================

const DashboardCard = ({ title, icon, children, status, statusText, onClear, onExport, variant = 'default' }) => (
    <div className={`${dashboardStyles.card} ${dashboardStyles[variant]}`}>
        <div className={dashboardStyles.cardHeader}>
            <div className={dashboardStyles.cardTitle}>
                <div className={dashboardStyles.cardIcon}>{icon}</div>
                <h3>{title}</h3>
            </div>
            <div className={dashboardStyles.cardActions}>
                {onExport && (
                    <button onClick={onExport} className={dashboardStyles.exportBtn} title="Exportar relatório">
                        <Icons.Download />
                    </button>
                )}
                {onClear && (
                    <button onClick={onClear} className={dashboardStyles.clearBtn}>
                        <Icons.Trash />
                    </button>
                )}
                {status && (
                    <div className={`${dashboardStyles.statusBadge} ${dashboardStyles[status]}`}>
                        <div className={dashboardStyles.statusDot} />
                        {statusText}
                    </div>
                )}
            </div>
        </div>
        <div className={dashboardStyles.cardContent}>{children}</div>
    </div>
);

const MetricsCard = ({ label, value, subtitle, color, icon, percentage, loading, trend, onClick }) => (
    <div className={dashboardStyles.metricsCard} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
        <div className={dashboardStyles.metricsHeader}>
            <div className={dashboardStyles.metricsIcon} style={{ backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
            {percentage !== undefined && !loading && (
                <span className={dashboardStyles.metricsPercentage} style={{ backgroundColor: `${color}15`, color: color }}>
                    {percentage}%
                </span>
            )}
        </div>
        <div className={dashboardStyles.metricsValue}>
            {loading ? '...' : value}
        </div>
        <div className={dashboardStyles.metricsLabel}>{label}</div>
        {subtitle && !loading && (
            <div className={dashboardStyles.metricsSubtitle}>
                <Icons.Clock />
                {subtitle}
            </div>
        )}
        {trend && !loading && (
            <div className={dashboardStyles.metricsTrend}>
                {trend.isPositive ? <Icons.TrendingUp /> : <Icons.TrendingDown />}
                <span className={trend.isPositive ? dashboardStyles.trendUp : dashboardStyles.trendDown}>
                    {trend.value}% vs semana anterior
                </span>
            </div>
        )}
    </div>
);

// ============================================
// ANALYZERS (Helper Functions)
// ============================================

const predictSLAViolation = (tickets) => {
    if (!tickets || tickets.length === 0) return null;

    const criticalByPriority = {
        'critico': { sla: 15, unit: 'minutes', label: '15min' },
        'critical': { sla: 15, unit: 'minutes', label: '15min' },
        'alto': { sla: 60, unit: 'minutes', label: '1h' },
        'high': { sla: 60, unit: 'minutes', label: '1h' },
        'medio': { sla: 4, unit: 'hours', label: '4h' },
        'medium': { sla: 4, unit: 'hours', label: '4h' },
        'baixo': { sla: 24, unit: 'hours', label: '24h' },
        'low': { sla: 24, unit: 'hours', label: '24h' }
    };

    const atRisk = [];

    tickets.forEach(ticket => {
        if (ticket.status === 'Fechado') return;

        const priorityKey = ticket.prioridadeRaw?.toLowerCase() || ticket.prioridade?.toLowerCase();
        const priorityConfig = criticalByPriority[priorityKey];
        if (!priorityConfig) return;

        if (ticket.createdAt?.toDate) {
            const timeOpen = (Date.now() - ticket.createdAt.toDate()) / (1000 * 60);
            const slaLimit = priorityConfig.sla * (priorityConfig.unit === 'hours' ? 60 : 1);
            const remaining = slaLimit - timeOpen;
            const remainingPercent = (remaining / slaLimit) * 100;

            if (remaining > 0 && remainingPercent < 20) {
                atRisk.push({
                    id: ticket.id,
                    subject: ticket.assunto || 'Sem assunto',
                    priority: ticket.prioridade || ticket.prioridadeRaw || 'N/A',
                    remaining: formatTime(remaining),
                    remainingPercent: Math.round(remainingPercent),
                    urgency: ticket.urgencia,
                    impact: ticket.impacto
                });
            }
        }
    });

    if (atRisk.length > 0) {
        return {
            id: 'sla_violation',
            type: 'critical',
            icon: React.createElement(Icons.Clock),
            title: `⚠️ ${atRisk.length} ${atRisk.length === 1 ? 'ticket' : 'tickets'} próximo(s) de violar SLA`,
            message: `Prioridades afetadas: ${[...new Set(atRisk.map(t => t.priority))].join(', ')}`,
            details: atRisk.slice(0, 3),
            action: 'Ver tickets em risco',
            severity: 'critical',
            recommendation: 'Reatribuir para plantão imediatamente'
        };
    }
    return null;
};

const analyzeMTTR = (tickets) => {
    const closedTickets = tickets?.filter(t =>
        t.status === 'Fechado' &&
        t.createdAt?.toDate
    ) || [];

    if (closedTickets.length === 0) return null;

    const currentMTTR = closedTickets.reduce((sum, t) => {
        const resolvedAt = t.resolvedAt?.toDate ? t.resolvedAt.toDate() : t.updatedAt?.toDate();
        if (!resolvedAt) return sum;
        const resolutionTime = (resolvedAt - t.createdAt.toDate()) / (1000 * 60);
        return sum + resolutionTime;
    }, 0) / closedTickets.length;

    const targetMTTR = 60;

    if (currentMTTR > targetMTTR) {
        const severity = currentMTTR > targetMTTR * 2 ? 'critical' : 'warning';

        return {
            id: 'mttr_alert',
            type: severity === 'critical' ? 'critical' : 'warning',
            icon: React.createElement(Icons.Zap),
            title: `⏱️ MTTR atual: ${Math.round(currentMTTR)}min (Meta: ${targetMTTR}min)`,
            message: `Tempo médio de resolução ${Math.round((currentMTTR / targetMTTR) * 100)}% acima da meta`,
            details: {
                current: Math.round(currentMTTR),
                target: targetMTTR,
                degradation: `${Math.round(((currentMTTR - targetMTTR) / targetMTTR) * 100)}% acima`
            },
            action: 'Analisar gargalos',
            severity: severity,
            recommendation: currentMTTR > 120
                ? 'Revisar processos críticos e automatizar respostas'
                : 'Otimizar fluxo de aprovação'
        };
    }
    return null;
};

const analyzeChangeFailure = (deployments, incidents) => {
    if (!deployments || deployments.length === 0) return null;

    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const recentDeployments = deployments.filter(d => {
        if (!d.timestamp?.toDate) return false;
        return d.timestamp.toDate() > sevenDaysAgo;
    });

    if (recentDeployments.length === 0) return null;

    const failedDeployments = recentDeployments.filter(d => d.rollback || d.failed || d.status === 'failed');
    const failureRate = (failedDeployments.length / recentDeployments.length) * 100;

    if (failureRate > 15) {
        const changesCausingIncidents = incidents?.filter(i => i.causedByDeployment)?.length || 0;

        return {
            id: 'change_failure',
            type: failureRate > 30 ? 'critical' : 'warning',
            icon: React.createElement(Icons.Refresh),
            title: `📉 Change Failure Rate: ${Math.round(failureRate)}% (Meta: <15%)`,
            message: `${failedDeployments.length} de ${recentDeployments.length} deployments falharam na última semana`,
            details: {
                failureRate: Math.round(failureRate),
                deployments: recentDeployments.length,
                failed: failedDeployments.length,
                incidentsCaused: changesCausingIncidents
            },
            action: 'Implementar canary deployments',
            severity: failureRate > 30 ? 'critical' : 'high',
            recommendation: 'Adicionar validação pré-deploy e rollback automático'
        };
    }
    return null;
};

const analyzeResourceSaturation = (infraMetrics) => {
    if (!infraMetrics || infraMetrics.length === 0) return null;

    const latestMetrics = infraMetrics[infraMetrics.length - 1];
    if (!latestMetrics) return null;

    const resources = {
        cpu: { current: latestMetrics.cpu || 0, threshold: 80, trend: latestMetrics.cpuTrend || 'stable' },
        memory: { current: latestMetrics.memory || 0, threshold: 85, trend: latestMetrics.memoryTrend || 'stable' },
        disk: { current: latestMetrics.disk || 0, threshold: 90, trend: latestMetrics.diskTrend || 'stable' }
    };

    const saturated = Object.entries(resources).filter(([_, value]) => {
        if (value.trend !== 'increasing') return false;
        const hoursToSaturate = ((value.threshold - value.current) / 5) * 60;
        return hoursToSaturate < 24;
    });

    if (saturated.length > 0) {
        return {
            id: 'resource_saturation',
            type: 'warning',
            icon: React.createElement(Icons.Cloud),
            title: `🚨 ${saturated.length} recurso(s) em rota de saturação`,
            message: `${saturated.map(s => s[0].toUpperCase()).join(', ')} com tendência de crescimento`,
            details: saturated.map(s => ({
                resource: s[0],
                current: s[1].current,
                threshold: s[1].threshold,
                forecast: `${Math.ceil(((s[1].threshold - s[1].current) / 5) * 60)} minutos`
            })),
            action: 'Ver métricas detalhadas',
            severity: 'high',
            recommendation: 'Aumentar capacidade ou otimizar workloads'
        };
    }
    return null;
};

const analyzeSecurityRisk = (vulnerabilities, tickets) => {
    if (!vulnerabilities || vulnerabilities.length === 0) return null;

    const criticalVulns = vulnerabilities.filter(v => {
        const score = v.cvss_score || 0;
        return score >= 7.0 && v.status !== 'Patched' && v.status !== 'Closed' && v.status !== 'Resolvido';
    });

    const unpatchedCritical = criticalVulns.filter(v => !v.patched && v.status !== 'Patched');

    const securityTickets = tickets?.filter(t =>
        (t.categoria === 'Segurança' || t.categoria === 'Security') &&
        t.status !== 'Fechado'
    ) || [];

    if (unpatchedCritical.length === 0 && securityTickets.length === 0) return null;

    const hasExploitPublic = unpatchedCritical.some(v => v.exploit_public);

    return {
        id: 'security_risk',
        type: hasExploitPublic ? 'critical' : 'warning',
        icon: React.createElement(Icons.Shield),
        title: `🔓 ${unpatchedCritical.length} vulnerabilidade(s) crítica(s) sem patch`,
        message: hasExploitPublic
            ? '🚨 EXPLOIT PÚBLICO DISPONÍVEL - Ação imediata necessária!'
            : `${securityTickets.length} tickets de segurança pendentes`,
        details: {
            vulnerabilities: unpatchedCritical,
            securityTicketsCount: securityTickets.length,
            hasExploit: hasExploitPublic
        },
        action: 'Criar Ticket de Segurança',
        severity: hasExploitPublic ? 'critical' : 'high',
        recommendation: hasExploitPublic
            ? 'Isolar sistemas afetados imediatamente e aplicar patch'
            : 'Agendar janela de manutenção para aplicar correções'
    };
};

const analyzeTeamWorkload = (tickets, teamMembers) => {
    if (!tickets || tickets.length === 0) return null;

    const activeTickets = tickets.filter(t =>
        t.status !== 'Fechado'
    );

    const engineerLoad = new Map();

    activeTickets.forEach(ticket => {
        const assignedTo = ticket.assignedToEmail || 'Não atribuído';
        if (!engineerLoad.has(assignedTo)) {
            engineerLoad.set(assignedTo, { active: 0, critical: 0 });
        }
        const data = engineerLoad.get(assignedTo);
        data.active++;
        const priority = ticket.prioridadeRaw?.toLowerCase() || ticket.prioridade?.toLowerCase();
        if (priority === 'critico' || priority === 'critical') {
            data.critical++;
        }
    });

    const loadArray = Array.from(engineerLoad.entries()).map(([engineer, data]) => ({
        engineer,
        activeTickets: data.active,
        criticalTickets: data.critical
    }));

    const avgLoad = loadArray.reduce((sum, l) => sum + l.activeTickets, 0) / loadArray.length;
    const overloaded = loadArray.filter(l => l.activeTickets > avgLoad * 1.5);

    if (overloaded.length > 0 && loadArray.length > 1) {
        return {
            id: 'team_workload',
            type: 'warning',
            icon: React.createElement(Icons.User),
            title: `👥 ${overloaded.length} engenheiro(s) sobrecarregado(s)`,
            message: `Média da equipe: ${Math.round(avgLoad)} tickets ativos`,
            details: overloaded.map(o => ({
                engineer: o.engineer === 'Não atribuído' ? 'Não atribuído' : o.engineer.split('@')[0],
                load: Math.round((o.activeTickets / avgLoad) * 100),
                criticalCount: o.criticalTickets
            })),
            action: 'Rever distribuição',
            severity: 'medium',
            recommendation: 'Reatribuir tickets automaticamente ou contratar reforço'
        };
    }
    return null;
};

const calculateDowntimeImpact = (incidents, monthlyRevenue = 1000000) => {
    if (!incidents || incidents.length === 0) return null;

    const revenuePerMinute = monthlyRevenue / (30 * 24 * 60);
    const last30Days = Date.now() - (30 * 24 * 60 * 60 * 1000);

    const recentIncidents = incidents.filter(i => {
        if (!i.startTime?.toDate) return false;
        return i.startTime.toDate() > last30Days;
    });

    if (recentIncidents.length === 0) return null;

    const totalCost = recentIncidents.reduce((sum, incident) => {
        const duration = incident.endTime?.toDate
            ? (incident.endTime.toDate() - incident.startTime.toDate()) / (1000 * 60)
            : 60;
        const lostRevenue = revenuePerMinute * duration;
        const engineeringCost = (incident.engineersInvolved || 2) * duration * (50 / 60);
        return sum + lostRevenue + engineeringCost;
    }, 0);

    if (totalCost > 50000) {
        return {
            id: 'downtime_cost',
            type: 'critical',
            icon: React.createElement(Icons.DollarSign),
            title: `💰 Custo estimado de downtime: ${formatCurrency(totalCost)} (últimos 30 dias)`,
            message: `${recentIncidents.length} incidentes no período`,
            details: {
                totalCost: formatCurrency(totalCost),
                incidentsCount: recentIncidents.length,
                averageCost: formatCurrency(totalCost / recentIncidents.length)
            },
            action: 'Calcular ROI de HA',
            severity: 'critical',
            recommendation: 'Investir em alta disponibilidade e automação',
            roiPotential: formatCurrency(totalCost * 0.7)
        };
    }
    return null;
};

const correlateIncidents = (incidents, alerts) => {
    if (!incidents || incidents.length < 2) return null;

    const patterns = [];
    const incidentTypes = new Map();

    incidents.forEach(incident => {
        const type = incident.type || incident.category || 'Unknown';
        incidentTypes.set(type, (incidentTypes.get(type) || 0) + 1);
    });

    for (const [type, count] of incidentTypes.entries()) {
        if (count >= 3) {
            patterns.push({
                type,
                count,
                percentage: Math.round((count / incidents.length) * 100)
            });
        }
    }

    if (patterns.length > 0) {
        return {
            id: 'incident_pattern',
            type: 'warning',
            icon: React.createElement(Icons.Target),
            title: '🔄 Padrão recorrente de incidentes identificado',
            message: `${patterns[0].type}: ${patterns[0].count} ocorrências (${patterns[0].percentage}% dos incidentes)`,
            details: {
                patterns
            },
            action: 'Criar runbook',
            severity: 'medium',
            recommendation: 'Automatizar resolução para incidentes recorrentes'
        };
    }
    return null;
};

const formatTime = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)}min`;
    const hours = minutes / 60;
    if (hours < 24) return `${Math.round(hours)}h`;
    const days = hours / 24;
    return `${Math.round(days)}d`;
};

const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

// ============================================
// STORAGE SERVICE
// ============================================

const DASHBOARD_STORAGE_PATH = 'dashboard-exports';

const storageService = {
    uploadReport: async (reportName, data, format = 'json') => {
        try {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const fileName = `${reportName}_${timestamp}.${format}`;
            const filePath = `${DASHBOARD_STORAGE_PATH}/${fileName}`;
            const storageRef = ref(storage, filePath);

            let blob;
            let contentType;

            if (format === 'json') {
                const jsonString = JSON.stringify(data, null, 2);
                blob = new Blob([jsonString], { type: 'application/json' });
                contentType = 'application/json';
            } else if (format === 'csv') {
                const csvString = convertToCSV(data);
                blob = new Blob([csvString], { type: 'text/csv' });
                contentType = 'text/csv';
            } else {
                throw new Error(`Unsupported format: ${format}`);
            }

            await uploadBytes(storageRef, blob, { contentType });
            const downloadURL = await getDownloadURL(storageRef);

            return {
                success: true,
                fileName,
                filePath,
                downloadURL,
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            console.error('Failed to upload report:', error);
            throw error;
        }
    },

    listExports: async () => {
        try {
            const folderRef = ref(storage, DASHBOARD_STORAGE_PATH);
            const result = await listAll(folderRef);

            const files = await Promise.all(
                result.items.map(async (itemRef) => {
                    const url = await getDownloadURL(itemRef);
                    return {
                        name: itemRef.name,
                        fullPath: itemRef.fullPath,
                        downloadURL: url,
                        size: null
                    };
                })
            );

            return files.sort((a, b) => b.name.localeCompare(a.name));
        } catch (error) {
            console.error('Failed to list exports:', error);
            return [];
        }
    },

    deleteExport: async (filePath) => {
        try {
            const fileRef = ref(storage, filePath);
            await deleteObject(fileRef);
            return { success: true };
        } catch (error) {
            console.error('Failed to delete export:', error);
            throw error;
        }
    }
};

const convertToCSV = (data) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return '';

    const items = Array.isArray(data) ? data : [data];
    const headers = Object.keys(items[0]);
    const csvRows = [headers.join(',')];

    for (const item of items) {
        const values = headers.map(header => {
            const value = item[header];
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') return JSON.stringify(value).replace(/,/g, ';');
            return String(value).replace(/,/g, ';');
        });
        csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
};

// ============================================
// MAIN COMPONENT
// ============================================

const TIDashboard = ({ user, onQuickAction }) => {
    const { data: tickets, loading: ticketsLoading } = useFirestore(TICKETS_COLLECTION, 'createdAt', 'desc', null, 500);
    const { data: deployments } = useFirestore(DEPLOYMENTS_COLLECTION, 'timestamp', 'desc', null, 100);
    const { data: incidents } = useFirestore(INCIDENTS_COLLECTION, 'startTime', 'desc', null, 100);
    const { data: infraMetrics } = useFirestore(INFRA_METRICS_COLLECTION, 'timestamp', 'desc', null, 100);
    const { data: vulnerabilities, loading: vulnLoading } = useFirestore(VULNERABILITIES_COLLECTION, 'cvss_score', 'desc', null, 50);
    const { data: teamMembers } = useFirestore(TEAM_MEMBERS_COLLECTION, 'name', 'asc', null, 50);
    const { addDocument: addTicket } = useFirestore(TICKETS_COLLECTION, 'createdAt', 'desc');

    // Presence hook for online users
    const { onlineUsers, loading: presenceLoading } = usePresence();

    const [systemStatus, setSystemStatus] = useState({ firebase: 'checking', auth: 'checking', storage: 'checking', network: 'checking' });
    const [pageErrors, setPageErrors] = useState([]);
    const [metrics, setMetrics] = useState({ uptime: '99.9%', avgResponseTime: '245ms', lastIncident: 'Nenhum' });
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [generatingReport, setGeneratingReport] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState(null);

    const isMounted = useRef(true);
    const healthCheckInterval = useRef(null);

    // Process data for charts
    const trendData = useMemo(() => {
        if (!tickets || tickets.length === 0) return [];

        const last14Days = new Array(14).fill().map((_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - (13 - i));
            date.setHours(0, 0, 0, 0);
            return date;
        });

        const ticketsByDate = tickets.reduce((acc, ticket) => {
            if (ticket.createdAt?.toDate) {
                const date = ticket.createdAt.toDate();
                date.setHours(0, 0, 0, 0);
                const dateKey = date.toISOString().split('T')[0];
                acc[dateKey] = (acc[dateKey] || 0) + 1;
            }
            return acc;
        }, {});

        return last14Days.map(date => ({
            date: date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
            fullDate: date,
            count: ticketsByDate[date.toISOString().split('T')[0]] || 0
        }));
    }, [tickets]);

    const priorityData = useMemo(() => {
        if (!tickets || tickets.length === 0) return [];

        const priorities = { 'Crítico': 0, 'Alta': 0, 'Média': 0, 'Baixa': 0 };

        tickets.forEach(ticket => {
            const rawPriority = ticket.prioridadeRaw?.toLowerCase() || ticket.prioridade?.toLowerCase();
            if (rawPriority === 'critico' || rawPriority === 'critical') {
                priorities['Crítico']++;
            } else if (rawPriority === 'alto' || rawPriority === 'high') {
                priorities['Alta']++;
            } else if (rawPriority === 'medio' || rawPriority === 'medium') {
                priorities['Média']++;
            } else if (rawPriority === 'baixo' || rawPriority === 'low') {
                priorities['Baixa']++;
            } else {
                const legacyPriority = ticket.priority;
                if (legacyPriority === 'Crítica' || legacyPriority === 'Critical') priorities['Crítico']++;
                else if (legacyPriority === 'Alta' || legacyPriority === 'High') priorities['Alta']++;
                else if (legacyPriority === 'Média' || legacyPriority === 'Medium') priorities['Média']++;
                else if (legacyPriority === 'Baixa' || legacyPriority === 'Low') priorities['Baixa']++;
            }
        });

        return Object.entries(priorities)
            .filter(([_, count]) => count > 0)
            .map(([priority, count]) => ({ priority, count }));
    }, [tickets]);

    const healthScore = useMemo(() => {
        if (!tickets || tickets.length === 0) return 100;

        let score = 100;

        const unresolvedCritical = tickets.filter(t => {
            const priority = t.prioridadeRaw?.toLowerCase() || t.prioridade?.toLowerCase();
            return (priority === 'critico' || priority === 'critical') &&
                t.status !== 'Fechado';
        }).length;
        score -= unresolvedCritical * 15;

        const unassignedCount = tickets.filter(t => !t.assignedToEmail && t.status !== 'Fechado').length;
        score -= unassignedCount * 5;

        const oldTickets = tickets.filter(t => {
            if (!t.createdAt?.toDate) return false;
            const daysOpen = (new Date() - t.createdAt.toDate()) / (1000 * 60 * 60 * 24);
            return daysOpen > 5 && t.status !== 'Fechado';
        }).length;
        score -= oldTickets * 3;

        return Math.max(0, Math.min(100, Math.round(score)));
    }, [tickets]);

    const stats = useMemo(() => {
        if (!tickets) {
            return { open: 0, inProgress: 0, closed: 0, myTickets: 0, unassigned: 0, total: 0, critical: 0 };
        }

        const critical = tickets.filter(t => {
            const priority = t.prioridadeRaw?.toLowerCase() || t.prioridade?.toLowerCase();
            return priority === 'critico' || priority === 'critical';
        }).length;

        return {
            open: tickets.filter(t => t.status === 'Aberto' || t.status === 'Open').length,
            inProgress: tickets.filter(t => t.status === 'Em Progresso' || t.status === 'In Progress').length,
            closed: tickets.filter(t => t.status === 'Fechado' || t.status === 'Closed').length,
            myTickets: tickets.filter(t => t.assignedToEmail === user?.email && t.status !== 'Fechado' && t.status !== 'Closed').length,
            unassigned: tickets.filter(t => !t.assignedToEmail).length,
            total: tickets.length,
            critical: critical,
        };
    }, [tickets, user?.email]);

    const unassignedTickets = useMemo(() => {
        return tickets?.filter(t => !t.assignedToEmail)?.slice(0, 5) || [];
    }, [tickets]);

    // Get online users count safely
    const onlineUsersCount = Array.isArray(onlineUsers) ? onlineUsers.length : 0;
    const onlineUsersList = Array.isArray(onlineUsers) ? onlineUsers : [];

    // Health Check
    const checkSystemHealth = useCallback(async () => {
        try {
            const realTimeMetrics = await healthService.getRealTimeMetrics();
            let storageStatus = 'checking';
            try {
                await storageService.listExports();
                storageStatus = 'connected';
            } catch (storageError) {
                storageStatus = 'error';
            }

            if (isMounted.current) {
                setSystemStatus({
                    ...realTimeMetrics.systemStatus,
                    storage: storageStatus
                });
                setMetrics({
                    uptime: realTimeMetrics.uptime,
                    avgResponseTime: realTimeMetrics.avgResponseTime,
                    lastIncident: realTimeMetrics.lastIncident,
                });
                setLastUpdated(new Date());
            }
        } catch (err) {
            console.error('Failed to check system health:', err);
        }
    }, []);

    // Create Security Ticket
    const createSecurityTicket = useCallback(async (vulnerability) => {
        if (!addTicket) {
            console.error('addTicket function not available');
            throw new Error('Sistema de tickets indisponível');
        }

        const cvssScore = vulnerability.cvss_score || 0;
        let priorityRaw = 'medio';
        if (cvssScore >= 9.0) priorityRaw = 'critico';
        else if (cvssScore >= 7.0) priorityRaw = 'alto';
        else if (cvssScore >= 4.0) priorityRaw = 'medio';
        else priorityRaw = 'baixo';

        const getSeverityLabel = (score) => {
            if (score >= 9.0) return 'Crítica';
            if (score >= 7.0) return 'Alta';
            if (score >= 4.0) return 'Média';
            return 'Baixa';
        };

        const ticketData = {
            assunto: `[SEGURANÇA] ${vulnerability.cve} - ${vulnerability.name || vulnerability.title || 'Vulnerabilidade Detectada'}`,
            descricao: `**CVE:** ${vulnerability.cve}
**CVSS Score:** ${cvssScore} (${getSeverityLabel(cvssScore)})
**Exploit Público:** ${vulnerability.exploit_public ? 'SIM - Ação Imediata!' : 'Não'}
**Status:** ${vulnerability.status || 'Pendente'}

**Descrição:**
${vulnerability.description || 'Sem descrição disponível'}

**Sistemas Afetados:**
${vulnerability.affected_systems?.join('\n') || vulnerability.affectedSystems?.join('\n') || 'Não especificado'}

**Remediação Recomendada:**
${vulnerability.remediation || vulnerability.solution || 'Avaliar e aplicar patch conforme política de segurança'}

**Referência:** ${vulnerability.reference_url || `https://nvd.nist.gov/vuln/detail/${vulnerability.cve}`}`,
            categoria: 'seguranca',
            prioridade: getSeverityLabel(cvssScore),
            prioridadeRaw: priorityRaw,
            urgencia: cvssScore >= 7.0 ? 'imediato' : 'urgente',
            impacto: 'todos',
            status: 'Aberto',
            userEmail: user?.email || 'security-scanner@empresa.com',
            userName: user?.nome || user?.email?.split('@')[0] || 'Security Scanner',
            userRole: user?.role || 'TI',
            assignedToEmail: null,
            assignedToName: null,
            timeSpentMinutes: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            vulnerability_cve: vulnerability.cve,
            vulnerability_score: cvssScore,
            vulnerability_exploit_public: vulnerability.exploit_public || false,
            ticket_type: 'security_auto'
        };

        await addTicket(ticketData);

        try {
            await storageService.uploadReport(
                `security_ticket_${vulnerability.cve}_${Date.now()}`,
                ticketData,
                'json'
            );
        } catch (storageError) {
            console.warn('Failed to backup ticket to storage:', storageError);
        }

        return true;
    }, [addTicket, user]);

    // Generate Full Report
    const generateFullReport = useCallback(async () => {
        setGeneratingReport(true);
        try {
            const reportData = {
                generatedAt: new Date().toISOString(),
                generatedBy: user?.email || 'Unknown',
                metrics: {
                    tickets: {
                        total: tickets?.length || 0,
                        open: stats.open,
                        inProgress: stats.inProgress,
                        closed: stats.closed,
                        unassigned: stats.unassigned,
                        critical: stats.critical,
                        myTickets: stats.myTickets
                    },
                    deployments: {
                        total: deployments?.length || 0,
                        recentWeek: deployments?.filter(d => {
                            if (!d.timestamp?.toDate) return false;
                            return d.timestamp.toDate() > Date.now() - (7 * 24 * 60 * 60 * 1000);
                        }).length || 0
                    },
                    incidents: {
                        total: incidents?.length || 0,
                        last30Days: incidents?.filter(i => {
                            if (!i.startTime?.toDate) return false;
                            return i.startTime.toDate() > Date.now() - (30 * 24 * 60 * 60 * 1000);
                        }).length || 0
                    },
                    vulnerabilities: {
                        total: vulnerabilities?.length || 0,
                        critical: vulnerabilities?.filter(v => (v.cvss_score || 0) >= 7.0).length || 0,
                        unpatched: vulnerabilities?.filter(v => v.status !== 'Patched' && v.status !== 'Closed').length || 0
                    },
                    healthScore: healthScore
                },
                priorityDistribution: priorityData,
                trendData: trendData
            };

            const result = await storageService.uploadReport(
                `dashboard_report_${new Date().toISOString().split('T')[0]}`,
                reportData,
                'json'
            );

            alert(`Relatório gerado com sucesso!\nArquivo: ${result.fileName}\nDisponível em: ${result.downloadURL}`);
        } catch (error) {
            console.error('Failed to generate report:', error);
            alert('Erro ao gerar relatório: ' + error.message);
        } finally {
            setGeneratingReport(false);
        }
    }, [tickets, deployments, incidents, vulnerabilities, user, healthScore, priorityData, trendData, stats]);

    const clearErrorLogs = useCallback(() => {
        if (window.confirm('Tem certeza que deseja limpar todos os logs de erro?')) {
            errorLoggingService.clearErrors();
            setPageErrors([]);
        }
    }, []);

    const handleMetricClick = useCallback((metricType, data) => {
        setSelectedMetric({ type: metricType, data });
        if (onQuickAction) {
            onQuickAction('view_metric', { type: metricType, data });
        }
    }, [onQuickAction]);

    // Effects
    useEffect(() => {
        isMounted.current = true;
        const cleanup = errorLoggingService.setupGlobalErrorHandling(user?.email);
        const recentErrors = errorLoggingService.getRecentErrors(10);
        if (isMounted.current) setPageErrors(recentErrors);
        const errorInterval = setInterval(() => {
            if (isMounted.current) {
                const errors = errorLoggingService.getRecentErrors(10);
                setPageErrors(errors);
            }
        }, 10000);
        return () => {
            isMounted.current = false;
            cleanup();
            clearInterval(errorInterval);
        };
    }, [user?.email]);

    useEffect(() => {
        checkSystemHealth();
        healthCheckInterval.current = setInterval(checkSystemHealth, 60000);
        return () => {
            if (healthCheckInterval.current) {
                clearInterval(healthCheckInterval.current);
            }
        };
    }, [checkSystemHealth]);

    const systemHealthStatus = Object.values(systemStatus).every(s => s === 'connected')
        ? 'operational'
        : Object.values(systemStatus).some(s => s === 'error') ? 'degraded' : 'checking';

    if (ticketsLoading && !tickets) {
        return (
            <div className={dashboardStyles.loadingContainer}>
                <div className={dashboardStyles.spinner} />
                <p>Carregando dashboard...</p>
            </div>
        );
    }

    return (
        <div className={dashboardStyles.dashboardContainer}>
            {/* Header */}
            <div className={dashboardStyles.dashboardHeader}>
                <div className={dashboardStyles.headerMeta}>
                    <div className={dashboardStyles.lastUpdate}>
                        <Icons.Clock />
                        Última atualização: {lastUpdated.toLocaleTimeString('pt-BR')}
                    </div>
                    <div className={dashboardStyles.userInfo}>
                        <Icons.User />
                        {user?.nome || user?.email}
                    </div>
                </div>
            </div>

            {/* Metrics Grid */}
            <div className={dashboardStyles.section}>
                <h2 className={dashboardStyles.sectionTitle}>Visão Geral de Tickets</h2>
                <div className={dashboardStyles.metricsGrid}>
                    <MetricsCard
                        label="Tickets Abertos"
                        value={stats.open}
                        color="#dc2626"
                        icon={<Icons.AlertCircle />}
                        percentage={stats.total > 0 ? Math.round((stats.open / stats.total) * 100) : 0}
                        loading={ticketsLoading}
                        onClick={() => handleMetricClick('open_tickets', { status: 'Aberto' })}
                    />
                    <MetricsCard
                        label="Em Progresso"
                        value={stats.inProgress}
                        color="#f59e0b"
                        icon={<Icons.Clock />}
                        percentage={stats.total > 0 ? Math.round((stats.inProgress / stats.total) * 100) : 0}
                        loading={ticketsLoading}
                        onClick={() => handleMetricClick('in_progress', { status: 'Em Progresso' })}
                    />
                    <MetricsCard
                        label="Fechados (30d)"
                        value={stats.closed}
                        color="#10b981"
                        icon={<Icons.CheckCircle />}
                        percentage={stats.total > 0 ? Math.round((stats.closed / stats.total) * 100) : 0}
                        loading={ticketsLoading}
                        onClick={() => handleMetricClick('closed_tickets', { status: 'Fechado' })}
                    />
                    <MetricsCard
                        label="Não Atribuídos"
                        value={stats.unassigned}
                        color="#8b5cf6"
                        icon={<Icons.User />}
                        percentage={stats.total > 0 ? Math.round((stats.unassigned / stats.total) * 100) : 0}
                        loading={ticketsLoading}
                        onClick={() => handleMetricClick('unassigned', { assigned: false })}
                    />
                    {/* Online Users Metric */}
                    <MetricsCard
                        label="Usuários Online"
                        value={onlineUsersCount}
                        color="#6366f1"
                        icon={<Icons.Users />}
                        loading={presenceLoading}
                        subtitle={`${onlineUsersCount} usuário${onlineUsersCount !== 1 ? 's' : ''} ativo${onlineUsersCount !== 1 ? 's' : ''} agora`}
                        onClick={() => handleMetricClick('online_users', { users: onlineUsersList })}
                    />
                </div>
            </div>

            {/* Charts */}
            <div className={dashboardStyles.section}>
                <h2 className={dashboardStyles.sectionTitle}>Análise & Tendências</h2>
                <div className={dashboardStyles.chartsGrid}>
                    <DashboardCard title="Volume de Tickets" icon={<ChartIcons.Activity />}>
                        <TrendChart data={trendData} />
                        <div className={dashboardStyles.chartInsight}>
                            <Icons.TrendingUp />
                            <span>Média últimos 7 dias: {(trendData.slice(-7).reduce((sum, d) => sum + d.count, 0) / 7).toFixed(1)} tickets/dia</span>
                        </div>
                    </DashboardCard>

                    <DashboardCard title="Distribuição por Prioridade" icon={<ChartIcons.PieChart />}>
                        <PriorityChart data={priorityData} />
                    </DashboardCard>
                </div>
            </div>

            {/* Health & System Status */}
            <div className={dashboardStyles.section}>
                <h2 className={dashboardStyles.sectionTitle}>Integridade & Performance</h2>
                <div className={dashboardStyles.healthGrid}>
                    <DashboardCard title="Health Score" icon={<Icons.Gauge />}>
                        <HealthScoreChart score={healthScore} loading={ticketsLoading} />
                    </DashboardCard>

                    <DashboardCard
                        title="Conexões"
                        icon={<Icons.Database />}
                        status={systemHealthStatus}
                        statusText={systemHealthStatus === 'operational' ? 'Operacional' : systemHealthStatus === 'degraded' ? 'Degradado' : 'Verificando'}
                    >
                        <div className={dashboardStyles.servicesList}>
                            {[
                                { label: 'Firebase / Firestore', status: systemStatus.firebase, icon: '🔥' },
                                { label: 'Autenticação', status: systemStatus.auth, icon: '🔐' },
                                { label: 'Storage', status: systemStatus.storage, icon: '💾' },
                                { label: 'Conexão de Rede', status: systemStatus.network, icon: '🌐' },
                            ].map((service, index) => (
                                <div key={index} className={dashboardStyles.serviceItem}>
                                    <span className={dashboardStyles.serviceIcon}>{service.icon}</span>
                                    <span className={dashboardStyles.serviceLabel}>{service.label}</span>
                                    <div className={dashboardStyles.serviceStatus}>
                                        <div className={`${dashboardStyles.statusDot} ${dashboardStyles[service.status]}`} />
                                        <span>
                                            {service.status === 'connected' ? 'Conectado' : service.status === 'error' ? 'Erro' : 'Verificando'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className={dashboardStyles.metricsInfo}>
                            <div className={dashboardStyles.metricItem}>
                                <span>Uptime</span>
                                <strong>{metrics.uptime}</strong>
                            </div>
                            <div className={dashboardStyles.metricItem}>
                                <span>Resposta</span>
                                <strong>{metrics.avgResponseTime}</strong>
                            </div>
                        </div>
                    </DashboardCard>

                    <DashboardCard
                        title="Erros de Página"
                        icon={<Icons.AlertCircle />}
                        onClear={pageErrors.length > 0 ? clearErrorLogs : null}
                    >
                        {pageErrors.length === 0 ? (
                            <div className={dashboardStyles.emptyState}>
                                <div className={dashboardStyles.emptyIcon}>
                                    <Icons.CheckCircle />
                                </div>
                                <p>Nenhum erro detectado</p>
                                <span>Todas as páginas estão funcionando corretamente</span>
                            </div>
                        ) : (
                            <div className={dashboardStyles.errorsList}>
                                {pageErrors.slice(0, 5).map((err) => (
                                    <div key={err.id} className={dashboardStyles.errorItem}>
                                        <div className={dashboardStyles.errorMessage}>
                                            <span className={dashboardStyles.errorText}>
                                                {err.message?.length > 60 ? err.message.substring(0, 60) + '...' : err.message}
                                            </span>
                                            <span className={dashboardStyles.errorTime}>{err.time}</span>
                                        </div>
                                        <div className={dashboardStyles.errorMeta}>
                                            <span>{err.source}</span>
                                            {err.line && <span>Linha {err.line}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </DashboardCard>
                </div>
            </div>

            {/* Online Users Section */}
            <div className={dashboardStyles.section}>
                <div className={dashboardStyles.sectionHeader}>
                    <h2 className={dashboardStyles.sectionTitle}>Usuários Online</h2>
                    <span className={dashboardStyles.sectionBadge}>
                        {onlineUsersCount} {onlineUsersCount === 1 ? 'ativo' : 'ativos'}
                    </span>
                </div>

                <div className={dashboardStyles.onlineUsersGrid}>
                    {onlineUsersList.length > 0 ? (
                        onlineUsersList.map((user) => (
                            <div key={user.id} className={dashboardStyles.onlineUserCard}>
                                <div className={dashboardStyles.userAvatar}>
                                    {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                                </div>
                                <div className={dashboardStyles.userInfo}>
                                    <span className={dashboardStyles.userName}>
                                        {user.displayName || user.email?.split('@')[0] || 'Usuário'}
                                    </span>
                                </div>
                                <div className={dashboardStyles.userStatus}>
                                    <span className={dashboardStyles.onlineDot} />
                                    Online
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={dashboardStyles.emptyState}>
                            <Icons.Users />
                            <p>Nenhum usuário online no momento</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Unassigned Tickets */}
            <div className={dashboardStyles.section}>
                <div className={dashboardStyles.sectionHeader}>
                    <h2 className={dashboardStyles.sectionTitle}>Aguardando Atribuição</h2>
                    <span className={dashboardStyles.sectionBadge}>{stats.unassigned} tickets</span>
                </div>

                <div className={dashboardStyles.ticketsTable}>
                    {unassignedTickets.length > 0 ? (
                        unassignedTickets.map((ticket) => {
                            const priorityInfo = getPriorityDisplay(ticket.prioridadeRaw || ticket.prioridade);
                            const categoryInfo = getCategoryDisplay(ticket.categoria);
                            const urgencyInfo = getUrgencyDisplay(ticket.urgencia);
                            const impactInfo = getImpactDisplay(ticket.impacto);
                            const timeInStatus = getTimeInStatus(ticket);

                            return (
                                <div key={ticket.id} className={dashboardStyles.ticketRow}>
                                    <div className={dashboardStyles.ticketInfo}>
                                        <div className={dashboardStyles.ticketHeader}>
                                            <div className={`${dashboardStyles.priorityDot} ${dashboardStyles[ticket.prioridadeRaw?.toLowerCase() || ticket.prioridade?.toLowerCase() || 'baixo']}`} />
                                            <strong>{ticket.assunto || 'Sem assunto'}</strong>
                                            <span className={`${dashboardStyles.priorityBadge} ${dashboardStyles[ticket.prioridadeRaw?.toLowerCase() || ticket.prioridade?.toLowerCase() || 'baixo']}`}>
                                                {priorityInfo.icon} {priorityInfo.label}
                                            </span>
                                            {ticket.categoria && (
                                                <span className={dashboardStyles.categoryBadge}>
                                                    {categoryInfo.icon} {categoryInfo.label}
                                                </span>
                                            )}
                                        </div>
                                        <div className={dashboardStyles.ticketMeta}>
                                            <span><Icons.User /> {ticket.userName || ticket.userEmail || 'Usuário'}</span>
                                            <span><Icons.Clock /> {ticket.createdAt?.toDate?.() ? ticket.createdAt.toDate().toLocaleDateString('pt-BR') : 'Data desconhecida'}</span>
                                            <span><Icons.ClockIcon /> Espera: {timeInStatus}</span>
                                            {ticket.urgencia && (
                                                <span><Icons.Clock /> {urgencyInfo.icon} {urgencyInfo.label}</span>
                                            )}
                                            {ticket.impacto && (
                                                <span><Icons.Target /> {impactInfo.label}</span>
                                            )}
                                            {ticket.followUps && ticket.followUps.length > 0 && (
                                                <span><Icons.MessageSquare /> {ticket.followUps.length} respostas</span>
                                            )}
                                        </div>
                                    </div>
                                    <span className={`${dashboardStyles.statusBadge} ${dashboardStyles[ticket.status?.toLowerCase() || 'aberto']}`}>
                                        {ticket.status}
                                    </span>
                                </div>
                            );
                        })
                    ) : (
                        <div className={dashboardStyles.emptyTable}>
                            <div className={dashboardStyles.emptyIcon}>
                                <Icons.CheckCircle />
                            </div>
                            <p>Todos os tickets foram atribuídos</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// CHART COMPONENTS
// ============================================

const TrendChart = ({ data }) => {
    if (!data || data.length === 0) {
        return (
            <div className={dashboardStyles.emptyChart}>
                <Icons.AlertCircle />
                <p>Dados insuficientes para exibir tendência</p>
            </div>
        );
    }

    const avgCount = data.reduce((sum, d) => sum + d.count, 0) / data.length;

    return (
        <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                    <linearGradient id="ticketGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                <Tooltip
                    contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px'
                    }}
                />
                <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} fill="url(#ticketGradient)" name="Tickets" />
                <Line type="monotone" dataKey={() => avgCount} stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="5 5" name="Média" dot={false} />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

const PriorityChart = ({ data }) => {
    const COLORS = {
        'Crítico': '#dc2626',
        'Alta': '#f97316',
        'Média': '#3b82f6',
        'Baixa': '#10b981'
    };

    if (!data || data.length === 0) {
        return (
            <div className={dashboardStyles.emptyChart}>
                <Icons.AlertCircle />
                <p>Nenhum ticket para análise</p>
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={300}>
            <PieChart>
                <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent, name }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                    outerRadius={100}
                    innerRadius={60}
                    dataKey="count"
                >
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.priority] || '#94a3b8'} stroke="#ffffff" strokeWidth={2} />
                    ))}
                </Pie>
                <Tooltip />
                <Legend />
            </PieChart>
        </ResponsiveContainer>
    );
};

const HealthScoreChart = ({ score, loading }) => {
    const getColor = (value) => {
        if (value >= 80) return '#10b981';
        if (value >= 60) return '#f59e0b';
        if (value >= 40) return '#f97316';
        return '#dc2626';
    };

    const getStatus = (value) => {
        if (value >= 80) return 'Excelente';
        if (value >= 60) return 'Bom';
        if (value >= 40) return 'Atenção';
        return 'Crítico';
    };

    const data = [{ name: 'Health Score', value: score, fill: getColor(score) }];

    if (loading) {
        return (
            <div className={dashboardStyles.loadingChart}>
                <div className={dashboardStyles.spinner} />
            </div>
        );
    }

    return (
        <div>
            <ResponsiveContainer width="100%" height={200}>
                <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="85%" barSize={12} data={data} startAngle={180} endAngle={-180}>
                    <RadialBar minAngle={15} background clockWise dataKey="value" cornerRadius={8} fill={getColor(score)} />
                    <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '34px', fontWeight: '700', fill: 'var(--help-text-primary)' }}>
                        {score}
                    </text>
                    <text x="50%" y="63%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '12px', fill: 'var(--help-text-secondary)' }}>
                        {getStatus(score)}
                    </text>
                </RadialBarChart>
            </ResponsiveContainer>
            <div className={dashboardStyles.healthLegend}>
                <div className={dashboardStyles.legendItem}>
                    <div className={dashboardStyles.legendColor} style={{ backgroundColor: '#10b981' }} />
                    <span>80-100% Excelente</span>
                </div>
                <div className={dashboardStyles.legendItem}>
                    <div className={dashboardStyles.legendColor} style={{ backgroundColor: '#f59e0b' }} />
                    <span>60-79% Bom</span>
                </div>
                <div className={dashboardStyles.legendItem}>
                    <div className={dashboardStyles.legendColor} style={{ backgroundColor: '#f97316' }} />
                    <span>40-59% Atenção</span>
                </div>
                <div className={dashboardStyles.legendItem}>
                    <div className={dashboardStyles.legendColor} style={{ backgroundColor: '#dc2626' }} />
                    <span>0-39% Crítico</span>
                </div>
            </div>
        </div>
    );
};

export default TIDashboard;